import moment from 'moment-timezone';

import { InteractionApiModel } from '../../../api/targeting/model/interaction-api.model';
import { InteractionType } from '../../../api/targeting/enum/interaction-type.enum';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { EmailEventApiModel } from '../../../api/distributions/model/email-event.api.model';
import {
    DistributionReportStat,
    distributionReportRelevantStatHierarchy as distStatHierarchy
} from '../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { DistributionSendFrom } from './distribution-send-from.model';

export class InteractionModel {
    id: string;
    type: InteractionType;
    typeString: string;
    icon: string;
    date: number;
    dateString: string;
    timeZone: string;
    summary: string;
    notes: string;

    contactId: string;
    outletId: string;
    entityType: EntityType;
    entityId: string;
    createdUserId: number;
    createdUsername: string;
    sentByUserId: number;
    sentByUsername: string;
    events: EmailEventApiModel[];
    topStat: DistributionReportStat;
    distId: number;
    grantId: number;
    sendFrom: DistributionSendFrom;

    readonly interactionTypeStringMap = new Map<InteractionType, string>([
        [InteractionType.DISTRIBUTION, 'Discover.interactionView.type.distribution'],
        [InteractionType.ADHOC_EMAIL, 'Discover.interactionView.type.email'],
        [InteractionType.ADHOC_PHONE, 'Discover.interactionView.type.phone'],
        [InteractionType.ADHOC_MESSAGE, 'Discover.interactionView.type.message'],
        [InteractionType.ADHOC_INBOUND_INQUIRY, 'Discover.interactionView.type.inquiry'],
        [InteractionType.ADHOC_OTHER, 'Discover.interactionView.type.other']
    ]);


    readonly interactionIconsMap = new Map<InteractionType, string>([
        [InteractionType.DISTRIBUTION, 'fa-kit-duotone fa-agpr-distribution'],
        [InteractionType.ADHOC_EMAIL, 'fa-regular fa-duotone fa-envelope'],
        [InteractionType.ADHOC_PHONE, 'fa-regular fa-duotone fa-phone'],
        [InteractionType.ADHOC_MESSAGE, 'fa-regular fa-duotone fa-comment-dots'],
        [InteractionType.ADHOC_INBOUND_INQUIRY, 'fa-regular fa-duotone fa-envelope'],
        [InteractionType.ADHOC_OTHER, 'fa-regular fa-duotone fa-square-ellipsis']
    ]);

    constructor(apiModel: InteractionApiModel) {
        if (apiModel) {
            this.id = apiModel.id;
            this.type = apiModel.type;
            this.typeString = this.interactionTypeStringMap.get(apiModel.type);
            this.icon = this.interactionIconsMap.get(apiModel.type);
            this.date = apiModel.date;
            this.dateString = moment(this.date).tz(apiModel.timeZone).format('MMM DD, YYYY HH:mm z');
            this.timeZone = apiModel.timeZone;
            this.summary = apiModel.summary;
            this.notes = apiModel.notes;
            this.contactId = apiModel.contactUuid;
            this.outletId = apiModel.outletUuid;
            this.entityType = apiModel.interactorType;
            this.entityId = apiModel.interactorType === EntityType.CONTACT ? apiModel.contactUuid : apiModel.outletUuid;
            this.createdUserId = apiModel.createdUserId;
            this.sentByUserId = apiModel.sentByUserId;
            this.events = apiModel.events;
            this.distId = apiModel.distributionId;
            this.grantId = apiModel.grantId;
            if (apiModel.sendFrom) {
                this.sendFrom = new DistributionSendFrom(apiModel.sendFrom);
            }

            if (this.events?.length > 0) {
                this.events.sort((a, b) => distStatHierarchy.indexOf(a.type) - distStatHierarchy.indexOf(b.type));

                const allEventTypes: DistributionReportStat[] = this.events.map(event => event.type);
                // Find the first (ie., highest priority) relevant stat in the event list
                const priorityStat = distStatHierarchy.find(relevantStat =>
                    allEventTypes.find(eventType => eventType === relevantStat)
                );
                // Fall back to PENDING if no other stat found
                this.topStat = priorityStat ?? DistributionReportStat.PENDING;

            }
        }
    }

    toApiModel(): InteractionApiModel {
        return {
            id: this.id,
            type: this.type,
            date: this.date,
            timeZone: this.timeZone,
            summary: this.summary || null,
            notes: this.notes || null,
            contactUuid: this.contactId,
            outletUuid: this.outletId,
            interactorType: this.entityType,
            createdUserId: this.createdUserId,
            events: this.events,
            grantId: this.grantId,
            distributionId: this.distId,
            sendFrom: this.sendFrom?.toApiModel()
        };
    }
}
